// Razorpay Payment Service for LinkedIn Profile Optimizer
// Production: ₹99 INR for India (Asia/Kolkata timezone), $1 USD elsewhere
// Development: FREE (auto-unlock)

import { track } from './analyticsEvents';

/**
 * Pick INR vs USD based on browser timezone.
 * Asia/Kolkata → ₹99, anyone else → $1.
 * Server validates the choice; this is just the suggestion.
 */
const getPriceForLocale = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz === 'Asia/Kolkata') {
      return { amount: 9900, currency: 'INR', display: '₹99' };
    }
  } catch (_) { /* fall through */ }
  return { amount: 100, currency: 'USD', display: '$1' };
};

// Best-effort fire-and-forget POST to log every payment attempt to MongoDB.
// Server-side gives us the failure side of the funnel (Razorpay only logs
// successes today). Never blocks the user-facing flow if it errors.
const logPaymentAttempt = (payload) => {
  const apiUrl = process.env.NODE_ENV === 'production'
    ? '/api/log-payment-attempt'
    : 'http://localhost:3001/api/log-payment-attempt';
  try {
    fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch (_) { /* swallow */ }
};

// Detect if running locally (localhost) vs production (deployed)
const isLocalDev = () => {
  if (typeof window !== 'undefined') {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  }
  return false;
};

const RPAY_KEY_ID = 'rzp_live_S2VSTb7Y8y181RAZOc';

// Payment amount: $1 USD in production, FREE in development
const PAYMENT_AMOUNT_USD = 1;
const PAYMENT_AMOUNT_DEV_INR = 0;

/**
 * Get raw payment amount (number only)
 */
const getPaymentAmountValue = () => {
  return isLocalDev() ? PAYMENT_AMOUNT_DEV_INR : PAYMENT_AMOUNT_USD;
};

/**
 * Get payment currency
 */
const getPaymentCurrency = () => {
  return isLocalDev() ? 'INR' : 'USD';
};

/**
 * Get payment amount in smallest unit for Razorpay
 */
const getPaymentAmountSmallestUnit = () => {
  return isLocalDev() ? 0 : 100; // 0 for dev, 100 cents ($1) for prod
};

/**
 * Check if we're in development mode (free access)
 */
const isDevelopmentMode = () => {
  return isLocalDev();
};

/**
 * Detect user's country using IP geolocation
 * Returns 'India' or the detected country name
 */
const detectCountry = async () => {
  try {
    // Use free IP geolocation API
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    console.log('🌍 [GEO] Detected country:', data.country_name);
    return data.country_name || '';
  } catch (error) {
    console.warn('⚠️ [GEO] Country detection failed:', error.message);
    return '';
  }
};

// Cache detected country
let cachedCountry = null;

/**
 * Get cached country or detect it
 */
const getCountry = async () => {
  if (cachedCountry !== null) return cachedCountry;
  cachedCountry = await detectCountry();
  return cachedCountry;
};

// NOTE: The client-side generateAndSendReport() was removed on 2026-04-21.
// Report email delivery is now server-side only — triggered by
// api/verify-razorpay-payment.js after a successful Razorpay payment, via
// api/send-audit-report.js. See docs/plans/2026-04-21-001-feat-replace-ai-brain-optimizer-coach-plan.md
// for the rationale: paid users were missing emails when the browser
// closed mid-flight. The server-side path is bulletproof against that.

/**
 * Load Razorpay SDK script dynamically
 */
const loadRazorpayScript = () => {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error('Failed to load Razorpay SDK'));
    document.body.appendChild(script);
  });
};

/**
 * Create order on backend.
 * Currency is passed in from getPriceForLocale() - server validates it
 * against an allowlist before creating the Razorpay order.
 */
const createOrder = async (userEmail, profileUrl, country, currency) => {
  try {
    const apiUrl = process.env.NODE_ENV === 'production'
      ? '/api/create-razorpay-order'
      : 'http://localhost:3001/api/create-razorpay-order';

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userEmail,
        profileUrl,
        country,
        currency
      })
    });

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to create order');
    }

    return data.order;
  } catch (error) {
    console.error('❌ [RAZORPAY] Create order error:', error);
    throw error;
  }
};

/**
 * Verify payment on backend
 */
const verifyPayment = async (paymentData) => {
  try {
    const apiUrl = process.env.NODE_ENV === 'production'
      ? '/api/verify-razorpay-payment'
      : 'http://localhost:3001/api/verify-razorpay-payment';

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(paymentData)
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ [RAZORPAY] Verify payment error:', error);
    throw error;
  }
};

/**
 * Initialize and open Razorpay payment modal
 * @param {Object} options - Payment options
 * @param {string} options.userEmail - User's email
 * @param {string} options.userName - User's name
 * @param {string} options.profileUrl - LinkedIn profile URL being analyzed
 * @param {Function} options.onSuccess - Callback on successful payment
 * @param {Function} options.onFailure - Callback on payment failure
 * @param {Function} options.onCancel - Callback when user closes modal
 */
const initiatePayment = async ({ userEmail, userName, profileUrl, triggerSource, onSuccess, onFailure, onCancel }) => {
  try {
    // Check if development mode (free access)
    if (isLocalDev()) {
      console.log('💳 [RAZORPAY] DEV MODE - Free access, auto-unlocking...');
      
      // In development, auto-unlock without payment
      // Simulate successful payment response
      const mockResponse = {
        razorpay_order_id: `dev_order_${Date.now()}`,
        razorpay_payment_id: `dev_payment_${Date.now()}`,
        razorpay_signature: 'dev_signature'
      };
      
      console.log('✅ [RAZORPAY] DEV MODE - Auto-unlocked successfully');
      onSuccess && onSuccess(mockResponse);
      return;
    }
    
    // Pricing: timezone-driven. India → ₹99, everyone else → $1.
    // Server-side validates the choice against an allowlist.
    const localePrice = getPriceForLocale();
    const priceLabel = `${localePrice.display} ${localePrice.currency}`;
    console.log(`💳 [RAZORPAY] Initiating payment (${priceLabel})...`);

    // Detect country for tax purposes (best-effort; doesn't gate the price)
    const country = await getCountry();
    console.log('🌍 [RAZORPAY] Customer country:', country || 'unknown');

    // Load Razorpay SDK
    await loadRazorpayScript();

    // Create order on backend
    const order = await createOrder(userEmail, profileUrl, country, localePrice.currency);
    console.log(`✅ [RAZORPAY] Order created: ${order.id} (${order.amount} ${order.currency})`);

    // Funnel instrumentation: GA4 event + server-side attempt log.
    // Fires once per Razorpay open(), regardless of outcome.
    track.paymentInitiated({ amount: order.amount, currency: order.currency, orderId: order.id });
    logPaymentAttempt({
      status: 'attempted',
      orderId: order.id,
      userEmail,
      profileUrl,
      amount: order.amount,
      currency: order.currency,
      country,
      triggerSource: triggerSource || 'unknown',
    });

    // Configure Razorpay options - USD currency
    const options = {
      key: RAZORPAY_KEY_ID,
      amount: order.amount,
      currency: order.currency,
      name: 'LinkedIn Profile Optimizer',
      description: `Unlock Full Analysis - ${priceLabel}`,
      order_id: order.id,
      image: window.location.origin + '/(transparent).png', // Growleads logo
      prefill: {
        email: userEmail,
        name: userName || ''
      },
      theme: {
        color: '#000000' // Black theme
      },
      modal: {
        ondismiss: () => {
          console.log('⚠️ [RAZORPAY] Payment modal closed by user');
          track.paymentDismissed({ amount: order.amount, currency: order.currency, orderId: order.id });
          logPaymentAttempt({
            status: 'dismissed',
            orderId: order.id,
            userEmail,
            profileUrl,
            amount: order.amount,
            currency: order.currency,
          });
          onCancel && onCancel();
        }
      },
      handler: async (response) => {
        console.log('✅ [RAZORPAY] Payment successful, verifying...');

        try {
          // Verify payment on backend - includes Zoho Books invoice creation
          const verification = await verifyPayment({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            userEmail,
            userName,
            profileUrl,
            country
          });

          if (verification.success) {
            console.log('✅ [RAZORPAY] Payment verified successfully!');
            if (verification.invoiceCreated) {
              console.log('📧 [ZOHO] Invoice created and emailed:', verification.invoiceNumber);
            }
            track.paymentSuccess({
              amount: order.amount,
              currency: order.currency,
              paymentId: response.razorpay_payment_id,
              orderId: order.id,
            });
            onSuccess && onSuccess(response);
          } else {
            console.error('❌ [RAZORPAY] Payment verification failed');
            track.paymentFailed({
              amount: order.amount,
              currency: order.currency,
              orderId: order.id,
              errorCode: 'VERIFICATION_FAILED',
              errorDescription: 'Backend signature verification failed',
            });
            logPaymentAttempt({
              status: 'failed',
              orderId: order.id,
              userEmail,
              profileUrl,
              amount: order.amount,
              currency: order.currency,
              errorCode: 'VERIFICATION_FAILED',
              errorDescription: 'Backend signature verification failed',
            });
            onFailure && onFailure(new Error('Payment verification failed'));
          }
        } catch (verifyError) {
          console.error('❌ [RAZORPAY] Verification error:', verifyError);
          // Even if verification fails on our end, payment may have succeeded
          // For better UX, we'll unlock anyway but log the error
          console.warn('⚠️ [RAZORPAY] Unlocking despite verification error (payment received)');
          track.paymentSuccess({
            amount: order.amount,
            currency: order.currency,
            paymentId: response.razorpay_payment_id,
            orderId: order.id,
          });
          onSuccess && onSuccess(response);
        }
      }
    };

    // Open Razorpay checkout
    const razorpay = new window.Razorpay(options);

    razorpay.on('payment.failed', (response) => {
      console.error('❌ [RAZORPAY] Payment failed:', response.error);
      const err = response.error || {};
      track.paymentFailed({
        amount: order.amount,
        currency: order.currency,
        orderId: order.id,
        errorCode: err.code,
        errorDescription: err.description,
      });
      logPaymentAttempt({
        status: 'failed',
        orderId: order.id,
        userEmail,
        profileUrl,
        amount: order.amount,
        currency: order.currency,
        errorCode: err.code,
        errorDescription: err.description,
        errorReason: err.reason,
        errorSource: err.source,
        errorStep: err.step,
      });
      onFailure && onFailure(err);
    });

    razorpay.open();
    
  } catch (error) {
    console.error('❌ [RAZORPAY] Initiation error:', error);
    onFailure && onFailure(error);
  }
};

/**
 * Get formatted payment amount for display.
 * Dev → "Free". Prod → "₹99" for India timezone, "$1" elsewhere.
 */
const getPaymentAmount = () => {
  if (isLocalDev()) return 'Free';
  return getPriceForLocale().display;
};

const razorpayService = {
  initiatePayment,
  getPaymentAmount,
  getPaymentAmountValue,
  getPriceForLocale,
  loadRazorpayScript,
  isDevelopmentMode
};

export default razorpayService;
